import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { applyHomeEndKey, Badge, Button, Input } from "@recruiter/shared";

import { engagePlatformKill, resumePlatformKill } from "../lib/api";
import {
  RESUME_CONFIRMATION,
  STOP_CONFIRMATION,
  canSubmitPlatformAction,
} from "../lib/platform-control";
import { adminKeys, platformKillQuery } from "../lib/queries";
import { AreaBoundary } from "../components/area-gate";
import { LoadingState, errorMessage } from "../components/states";
import "./platform-control.css";

type PlatformControlProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** May this operator actually stop or resume Mira? `system_admin` at `read`
   *  sees the card and gets no form: `GET /platform/status` is the area's read
   *  half ("is Mira stopped right now" is a fact about the platform), while
   *  engaging and resuming are `full`. This card is the ONLY surface that renders
   *  that fact, so hiding the whole panel from a read-only holder would make the
   *  read half unreachable — and the deps, `AREA_LEVELS` and web/ARCHITECTURE.md
   *  all promise it is not. */
  canAct: boolean;
  /** Has the grant query answered yet? `canAct` is false while it is in flight,
   *  which is the safe default for a CONTROL but a lie as a SENTENCE: without
   *  this flag the panel tells a full-access operator their access is read only
   *  for as long as the fetch takes. The claim waits; the form still does not
   *  render early. */
  accessKnown: boolean;
};

function statusDate(value: string | null | undefined): string {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

/** A deliberate disclosure inside System Admin. Keeping the network read behind
 * the red trigger avoids an incident-control fetch for every permissions visit. */
export function PlatformControl({ open, onOpenChange, canAct, accessKnown }: PlatformControlProps) {
  const queryClient = useQueryClient();
  const status = useQuery({ ...platformKillQuery, enabled: open });
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!status.data) throw new Error("Platform status is unavailable.");
      if (status.data.killed) return resumePlatformKill({ confirm: confirmation });
      return engagePlatformKill({ confirm: confirmation, reason: reason.trim() });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.platformKill });
      setReason("");
      setConfirmation("");
      setActionError(null);
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  if (!open) {
    return (
      <section className="ad-platform-trigger" aria-labelledby="red-button-title">
        <div>
          <p className="ad-platform-trigger__eyebrow">Incident control</p>
          <h2 id="red-button-title" className="ad-section__title">
            Platform emergency stop
          </h2>
          <p className="ad-platform-trigger__copy">
            Stops Mira spend platform-wide. Use only during an active incident.
          </p>
        </div>
        <Button variant="solid" tone="danger" onClick={() => onOpenChange(true)}>
          Red Button - Disable Entire System
        </Button>
      </section>
    );
  }

  if (status.isPending) return <LoadingState label="Reading platform status…" />;
  if (status.isError) {
    return (
      <AreaBoundary
        area="system_admin"
        error={status.error}
        onRetry={() => void status.refetch()}
      />
    );
  }

  const killed = status.data.killed;
  const expectedConfirmation = killed ? RESUME_CONFIRMATION : STOP_CONFIRMATION;
  const canSubmit = canSubmitPlatformAction({
    killed,
    reason,
    confirmation,
    busy: mutation.isPending,
  });

  return (
    <section
      className={`ad-platform-control ad-platform-control--${killed ? "stopped" : "running"}`}
      aria-labelledby="platform-control-title"
    >
      <header className="ad-platform-control__head">
        <div>
          <p className="ad-platform-trigger__eyebrow">Lever A: spend</p>
          <h2 id="platform-control-title" className="ad-section__title">
            Red Button
          </h2>
          <p className="ad-platform-control__intro">
            {killed
              ? "Mira is stopped and refusing new work across the platform."
              : "Mira is running normally. Engaging this control stops all new work."}
          </p>
        </div>
        <div className="ad-platform-control__head-actions">
          <Badge tone={killed ? "danger" : "success"} variant="soft">
            {killed ? "STOPPED" : "Running"}
          </Badge>
          <Button
            variant="ghost"
            tone="neutral"
            size="sm"
            disabled={mutation.isPending}
            onClick={() => void status.refetch()}
          >
            Refresh status
          </Button>
          <Button
            variant="ghost"
            tone="neutral"
            size="sm"
            disabled={mutation.isPending}
            onClick={() => onOpenChange(false)}
          >
            Hide controls
          </Button>
        </div>
      </header>

      <div className="ad-platform-control__grid">
        <section className="ad-platform-control__state" aria-label="Platform state">
          <p className="ad-platform-control__state-label">Current system state</p>
          <strong>{killed ? "Platform stopped" : "Platform running"}</strong>
          {killed ? (
            <dl className="ad-platform-control__details">
              <div>
                <dt>Reason</dt>
                <dd>{status.data.reason || "Not recorded"}</dd>
              </div>
              <div>
                <dt>Engaged by</dt>
                <dd>{status.data.actor_email || "Not recorded"}</dd>
              </div>
              <div>
                <dt>Engaged at</dt>
                <dd>{statusDate(status.data.engaged_at)}</dd>
              </div>
            </dl>
          ) : (
            <p className="ad-platform-control__state-copy">
              Use the incident checklist before stopping traffic or resuming service.
            </p>
          )}
        </section>

        {!accessKnown ? (
          <section className="ad-platform-control__action" aria-label="Incident control access">
            <h3>{killed ? "Resume Mira" : "Disable the entire system"}</h3>
            <p>Checking your access to System Admin.</p>
          </section>
        ) : !canAct ? (
          <section className="ad-platform-control__action" aria-label="Incident control access">
            <h3>{killed ? "Resume Mira" : "Disable the entire system"}</h3>
            <p>
              Your access to System Admin is read only. You can see whether Mira is running, and
              stopping or resuming it needs Full access.
            </p>
          </section>
        ) : (
          <form
            className="ad-platform-control__action"
            onSubmit={(event) => {
              event.preventDefault();
              if (canSubmit) mutation.mutate();
            }}
          >
            <h3>{killed ? "Resume Mira" : "Disable the entire system"}</h3>
            <p>
              {killed
                ? "Resume only after the incident is contained. Jobs dropped while stopped are not replayed."
                : "This immediately stops Mira from accepting work. A Slack alert is sent to the ops channel."}
            </p>
            {!killed ? (
              <label className="ad-field">
                <span className="ad-field__label">Incident reason (5 to 500 characters)</span>
                <textarea
                  className="ad-textarea"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  onKeyDown={applyHomeEndKey}
                  minLength={5}
                  maxLength={500}
                  required
                />
                <span className="ad-platform-control__counter">{reason.trim().length}/500</span>
              </label>
            ) : null}
            <label className="ad-field">
              <span className="ad-field__label">
                Type <code>{expectedConfirmation}</code> to confirm
              </span>
              <Input
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="off"
              />
            </label>
            {actionError ? (
              <p className="ad-platform-control__error" role="alert">
                {actionError}
              </p>
            ) : null}
            <Button
              type="submit"
              variant="solid"
              tone={killed ? "success" : "danger"}
              disabled={!canSubmit}
            >
              {mutation.isPending ? "Working…" : killed ? "Resume Mira" : "STOP MIRA"}
            </Button>
          </form>
        )}
      </div>

      <aside className="ad-platform-control__lever-b">
        <div>
          <p className="ad-platform-trigger__eyebrow">Lever B: traffic</p>
          <h3>Stop traffic alongside spend</h3>
        </div>
        <ol>
          <li>
            Open <a href="https://rollout.fiverr-gw.com">Rollout</a> and find the
            <code> crows_pythons_agentic_briefs</code> flag.
          </li>
          <li>
            Set percentage to 0 and remove allowlisted IDs: 195154209, 111678507, 146437427. The
            allowlist wins over percentage.
          </li>
          <li>
            Rollout clients cache for about 60 seconds. In-flight buyers see Mira&apos;s maintenance
            page until it propagates, then the legacy brief flow.
          </li>
          <li>
            Resume Mira first, verify <code>/health</code> and a live turn, then restore the rollout
            flag.
          </li>
        </ol>
      </aside>
    </section>
  );
}
